<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import IconBase from '@/components/icon/IconBase.vue'
import IconClosedCaption from '@/components/icon/IconClosedCaption.vue'
import IconArrowRight from '@/components/icon/IconArrowRight.vue'
import IconBin from '@/components/icon/IconBin.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import StatusPill from '@/components/Atoms/StatusPill.vue'
import { languageName, summarizeFlowStatus, windowOf, formatDuration } from './captionLanguages'
import { isSyncWordsFlow, syncwordsHeadline, syncwordsLanguageName, languagesSummary } from './captionSyncWords'

const props = defineProps({
	flow: { type: Object, required: true },
	// Latest `GET /captions/:id/status` payload; only fetched by the list when
	// core sent no `summary` on the flow (older core).
	status: { type: Object, default: null },
	compact: { type: Boolean, default: false },
})

const emit = defineEmits(['delete'])

const router = useRouter()

const removed = computed(() => !!props.flow.streamDeleted)
const syncwords = computed(() => isSyncWordsFlow(props.flow))
const headline = computed(() => (syncwords.value ? syncwordsHeadline(props.flow, props.status) : summarizeFlowStatus(props.flow, props.status)))

// The card shows one of five coarse states: Off, On, Live, Stalled, Ended.
// The detailed ones (Starting, Waiting for stream, Session ended, Stopped)
// stay on the manage page's status strip, which has room to explain them;
// here the full label is only kept on the badge's `title`. Unknown /
// Checking (service unreachable, or a legacy core still being asked) fall
// through as-is.
const CARD_STATES = {
	off: { label: 'Off', variant: 'muted' },
	unconfigured: { label: 'Off', variant: 'muted' },
	starting: { label: 'On', variant: 'accent' },
	'waiting-stream': { label: 'On', variant: 'accent' },
	running: { label: 'On', variant: 'accent' },
	live: { label: 'Live', variant: 'success' },
	stalled: { label: 'Stalled', variant: 'warning' },
	expired: { label: 'Ended', variant: 'muted' },
	stopped: { label: 'Ended', variant: 'muted' },
}
// A SyncWords flow has one service state; the card shows it as is (from `summary.engineState`).
const SYNCWORDS_CARD_STATES = {
	off: { label: 'Off', variant: 'muted' },
	starting: { label: 'Starting', variant: 'accent' },
	'waiting-input': { label: 'Waiting for input', variant: 'warning' },
	live: { label: 'Live', variant: 'success' },
	error: { label: 'Error', variant: 'error' },
}
const badge = computed(() => (syncwords.value ? SYNCWORDS_CARD_STATES : CARD_STATES)[headline.value.key] || { label: headline.value.label, variant: 'muted' })

function requestDelete(event) {
	event?.stopPropagation?.()
	emit('delete', props.flow)
}

const nameOf = (code) => (syncwords.value ? syncwordsLanguageName(code) : languageName(code))
const languages = computed(() => (props.flow.langs || []).map((code) => ({
	code,
	name: nameOf(code),
	translated: !!props.flow.source && code.toLowerCase() !== props.flow.source.toLowerCase(),
})))
const footerLabel = computed(() => {
	if (!props.flow.configured) return 'Not set up'
	if (syncwords.value) return languagesSummary(props.flow)
	return `${languages.value.length} ${languages.value.length === 1 ? 'language' : 'languages'}`
})

// Time left in a timed caption window from the list `summary.window`. The list
// is not polled, so this is what core computed at load ("Ends in 42m"). An
// unlimited window shows nothing: the badge already says it is on.
const windowLabel = computed(() => {
	if (props.status || props.flow.summary === undefined || !props.flow.summary?.window) return ''
	const win = windowOf(null, props.flow)
	if (win.state !== 'running') return ''
	if (win.durationSec === null) return ''
	if (!Number.isFinite(win.remainingSec)) return ''
	if (win.remainingSec <= 0) return 'Ending'
	// Coarse on the card: whole minutes (the list is not live-updated).
	const minutes = Math.ceil(win.remainingSec / 60)
	return `Ends in ${minutes < 60 ? `${minutes}m` : formatDuration(minutes * 60)}`
})

const target = computed(() => ({ name: 'CaptioningManage', params: { flowId: props.flow.id } }))

function open(event) {
	if (event) {
		const interactive = event.target.closest?.('button, a[href], input, select, textarea')
		if (interactive) return
	}
	router.push(target.value)
}

function onKeydown(event) {
	if (event.key === 'Enter' || event.key === ' ') {
		event.preventDefault()
		router.push(target.value)
	}
}
</script>

<template>
	<article
		role="button"
		tabindex="0"
		class="captioning-card group relative flex w-full cursor-pointer rounded-xl bg-surface-2 text-left outline-none transition-[box-shadow,transform,background-color] duration-200 ease-out focus-visible:ring-2 focus-visible:ring-main"
		:class="compact
			? 'items-center gap-3 px-3 py-2 hover:bg-surface-3'
			: 'flex-col gap-4 p-4 shadow-[0_1px_3px_rgba(0,0,0,0.15),0_10px_28px_-8px_rgba(0,0,0,0.4)] hover:-translate-y-0.5 hover:shadow-[0_2px_6px_rgba(0,0,0,0.18),0_20px_44px_-10px_rgba(0,0,0,0.5)]'"
		:aria-label="`Manage captioning for ${flow.streamName || flow.streamKey}`"
		@click="open"
		@keydown="onKeydown"
	>
		<div class="flex min-w-0 flex-1 gap-3" :class="compact ? 'items-center' : 'items-start'">
			<span
				class="flex shrink-0 items-center justify-center rounded-lg bg-surface-3 text-main-light ring-1 ring-white/5"
				:class="compact ? 'size-9' : 'size-11'"
				aria-hidden="true"
			>
				<icon-base :width="compact ? 16 : 20" :height="compact ? 16 : 20" fill="none">
					<icon-closed-caption />
				</icon-base>
			</span>
			<div class="flex min-w-0 flex-1 flex-col" :class="compact ? 'gap-0.5' : 'gap-1'">
				<!-- The name gets the full row and may wrap to two lines; the status sits under it. -->
				<h3
					class="m-0 min-w-0 line-clamp-2 break-words font-medium leading-snug text-light-1"
					:class="compact ? 'text-sm md:text-base' : 'text-lg'"
					:title="flow.streamName"
					data-testid="card-name"
				>
					{{ flow.streamName || 'Untitled stream' }}
				</h3>
				<div class="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
					<span
						v-if="removed"
						class="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap text-tiny text-error-light"
						:data-status="headline.key"
					>
						<span class="size-1.5 rounded-full bg-error" aria-hidden="true" />
						{{ headline.label }}
					</span>
					<StatusPill
						v-else
						:variant="badge.variant"
						:pulse="headline.key === 'starting'"
						size="sm"
						class="shrink-0"
						:data-status="headline.key"
						:title="badge.label === headline.label ? undefined : headline.label"
					>
						{{ badge.label }}
					</StatusPill>
					<span v-if="windowLabel" class="shrink-0 whitespace-nowrap text-[11px] tabular-nums text-surface-7" data-testid="card-window">{{ windowLabel }}</span>
					<template v-if="compact && !removed">
						<span v-if="languages.length" class="text-[11px] text-surface-6" aria-hidden="true">·</span>
						<ul v-if="languages.length" class="m-0 flex list-none flex-wrap gap-1 p-0">
							<li
								v-for="lang in languages"
								:key="lang.code"
								class="rounded-full bg-surface-3 px-2 py-0.5 text-[11px] font-medium"
								:class="lang.translated ? 'text-surface-8' : 'text-surface-text-1'"
								:title="lang.translated ? `Translated from ${nameOf(flow.source)}` : 'Spoken language'"
							>
								{{ lang.name }}
							</li>
						</ul>
					</template>
				</div>
				<p v-if="removed && compact" class="m-0 text-[11px] text-surface-8">Captions no longer run. Delete this flow to clean up.</p>
			</div>
		</div>

		<template v-if="!compact && removed">
			<p class="m-0 text-tiny text-surface-8">
				The livestream this flow was linked to has been deleted. Captions no longer run; delete the flow and create a new one for another stream.
			</p>
			<div class="mt-auto flex items-center justify-between gap-2 border-t border-surface-3 pt-3 text-tiny text-surface-8">
				<span>Stream removed</span>
				<FormButton type="danger" size="sm" class="gap-1.5" aria-label="Delete captioning" @click="requestDelete">
					<icon-base><icon-bin /></icon-base>
					Delete
				</FormButton>
			</div>
		</template>
		<template v-else-if="!compact">
			<ul v-if="languages.length" class="m-0 flex list-none flex-wrap gap-1.5 p-0">
				<li
					v-for="lang in languages"
					:key="lang.code"
					class="inline-flex items-center gap-1 rounded-full bg-surface-3 px-2.5 py-1 text-tiny font-medium"
					:class="lang.translated ? 'text-surface-8' : 'text-surface-text-1'"
					:title="lang.translated ? `Translated from ${nameOf(flow.source)}` : 'Spoken language'"
				>
					<span>{{ lang.name }}</span>
					<span v-if="lang.translated" class="text-[10px] uppercase tracking-wide text-surface-6">translated</span>
				</li>
			</ul>
			<p v-else class="m-0 text-tiny text-surface-7">No languages chosen yet. Open to set up captions.</p>

			<div class="mt-auto flex items-center justify-between gap-2 border-t border-surface-3 pt-3 text-tiny text-surface-8">
				<span>{{ footerLabel }}</span>
				<span class="inline-flex items-center gap-1 font-medium text-surface-text-1 transition-colors group-hover:text-white">
					Manage
					<icon-base width="14" height="14" width-view-box="24" height-view-box="24" fill="none">
						<icon-arrow-right />
					</icon-base>
				</span>
			</div>
		</template>
		<FormButton v-else-if="removed" type="danger" is-icon size="sm" class="ml-auto shrink-0" aria-label="Delete captioning" @click="requestDelete">
			<icon-base><icon-bin /></icon-base>
		</FormButton>
		<span v-else class="ml-auto shrink-0 text-surface-7 transition-colors group-hover:text-white">
			<icon-base width="14" height="14" width-view-box="24" height-view-box="24" fill="none">
				<icon-arrow-right />
			</icon-base>
		</span>
	</article>
</template>
